import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';
import { newIdempotencyKey } from '../client/idempotency';
/**
 * Org-admin seat management (spec §15).
 *
 * Built by the backend as four `mine` routes with **no capability on any of
 * them** — the resource boundary is in the route, so the caller never names an
 * organisation and there is no id to substitute for another one.
 *
 * "Mine" needs BOTH `platformRole === 'ORG_ADMIN'` AND a live seat. Membership
 * alone would let any of nineteen ordinary parents revoke their colleagues'.
 */
export const Organisation = z.object({
    id: z.string().uuid(),
    name: z.string(),
    type: z.string(),
    seatCount: z.number().int().nonnegative(),
    seatsAssigned: z.number().int().nonnegative(),
    seatsAvailable: z.number().int().nonnegative(),
    billingEmail: z.string(),
    billingContactName: z.string().nullable(),
    renewalAt: z.string().datetime().nullable(),
});
/**
 * A seat, and **who holds it**.
 *
 * ── `user` was missing, and Zod removed the evidence ──────────────────────
 * This declared `id / userId / email / assignedAt / revokedAt`. The API also
 * sends `user`, which is where a claimed seat's name and address live:
 *
 * ```json
 * { "userId": "8b77ea5b…", "email": null,
 *   "user": { "id": "8b77ea5b…", "fullName": "Élise Gagnon",
 *             "email": "parent5@demo.laboussle.test" } }
 * ```
 *
 * Zod strips what it is not told about, so the object reaching the screen had
 * no `user` and the row fell back to `email` — which is `null` for every seat
 * that resolved to an account. The org admin's only working screen listed
 * eleven seats as `—`, on the page where they choose which one to revoke.
 *
 * The mirror of the `Event.author` lesson: that was a field we declared and the
 * API never sent, and it read as blank. This is a field the API sends and we
 * never declared, and it read as nothing. Both silent, both Zod doing exactly
 * as instructed.
 *
 * ── `status`, and NOT `userId === null` ───────────────────────────────────
 * Round 15 changed the invited shape. An invitation now creates a `PENDING`
 * account up front — so the address is resolved to a person *before* the seat
 * is written, and there is nothing to guess at later — which means an invited
 * seat carries a `userId` and a `user`:
 *
 * ```json
 * { "userId": "…", "email": "someone@cpe.ca", "status": "PENDING",
 *   "user": { "id": "…", "fullName": "", "email": "someone@cpe.ca" } }
 * ```
 *
 * `fullName` is the **empty string** until they accept, because an invitation
 * knows an address and not a name. Two things follow, and both were wrong here
 * an hour ago: `userId === null` no longer detects a pending seat, and a
 * truthy check on `user` no longer means there is a name to print.
 *
 * The backend's instruction is exact — *"Render `status`, not
 * `userId === null`"* — and `status` is derived server-side from `revokedAt`
 * and the holder's account status rather than stored, so it cannot drift out
 * of step with them.
 */
export const OrgSeatStatus = z.enum(['PENDING', 'ACTIVE', 'REVOKED']);
export const OrgSeatUser = z.object({
    id: z.string().uuid(),
    fullName: z.string(),
    email: z.string(),
});
export const OrgSeat = z.object({
    id: z.string().uuid(),
    userId: z.string().uuid().nullable(),
    /** The address the seat was invited to — kept as the label while `fullName` is empty. */
    email: z.string().nullable(),
    user: OrgSeatUser.nullable().optional(),
    status: OrgSeatStatus,
    assignedAt: z.string().datetime(),
    revokedAt: z.string().datetime().nullable(),
});
export function useMyOrganisation(http, enabled) {
    return useQuery({
        queryKey: ['organisations', 'mine'],
        enabled,
        queryFn: () => http.get('/organisations/mine', { schema: Organisation }),
        retry: false,
    });
}
export function useMySeats(http, enabled) {
    return useQuery({
        queryKey: ['organisations', 'mine', 'seats'],
        enabled,
        queryFn: () => http.get('/organisations/mine/seats', { schema: z.object({ items: z.array(OrgSeat) }) }),
        retry: false,
    });
}
/**
 * Both writes answer THE WHOLE SEAT LIST, not the row they touched.
 *
 * ── The bug this closes ───────────────────────────────────────────────────
 * Both mutations declared `schema: OrgSeat` — a bare object. The routes answer
 * `{ items: [...] }`, so Zod rejected every response, the mutation entered its
 * error state, and the screen said **"The seat couldn't be assigned."** over a
 * seat the server had just created. Measured: the refusal rendered, and
 * `org_seats` held the new row. An org admin retries, and the second attempt is
 * answered `SEAT_ALREADY_HELD`.
 *
 * That is the worst shape this class of defect takes — worse than the four
 * undeclared single-object envelopes recorded on `CohortEnvelope`, because
 * those failed a READ and this one reports failure on a WRITE that succeeded.
 *
 * ── Why it survived the org-admin pass ────────────────────────────────────
 * `docs/parity/16-org-admin.md` drove the assign with `curl` rather than
 * through the screen, so the route's behaviour was verified and this client's
 * reading of it never was. Found 2026-09-08 by driving `laboussle-mobile`'s
 * Seats screen, a straight port of this one — and fixed in both.
 *
 * The list is the right answer for a write that changes seat counts, so it is
 * declared rather than narrowed. The `onSuccess` invalidation stays: the
 * organisation's own totals live on a different read.
 */
const SeatList = z.object({ items: z.array(OrgSeat) });
export function useAssignSeat(http) {
    const queryClient = useQueryClient();
    return useMutation({
        /** A `userId` OR an `email`, never both and never neither. */
        mutationFn: (input) => http.post('/organisations/mine/seats/assign', input, {
            schema: SeatList,
            idempotencyKey: newIdempotencyKey(),
        }),
        onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['organisations', 'mine'] }),
    });
}
export function useRevokeSeat(http) {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (seatId) => http.post(`/organisations/mine/seats/${seatId}/revoke`, {}, {
            schema: SeatList,
            idempotencyKey: newIdempotencyKey(),
        }),
        onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['organisations', 'mine'] }),
    });
}
/**
 * Whether a seat may be revoked by the caller.
 *
 * Revoking your OWN seat is what makes you stop being an org admin, so it would
 * lock you out of the screen you are standing on — recoverable only by somebody
 * holding `finance.act`, which is the exact dependency these routes exist to
 * remove. The server refuses it with `SEAT_REVOKE_SELF`; we hide the action so
 * nobody discovers the refusal by hitting it.
 *
 * Same shape as the rule that stops the last holder of `team.manage` being
 * revoked.
 */
export function canRevokeSeat(seat, viewerUserId) {
    return seat.revokedAt === null && seat.userId !== viewerUserId;
}
/**
 * How to name the person on a seat row.
 *
 * * **accepted** — a real `fullName`, so the name with the address under it. A
 *   name is what an admin recognises; the address is what makes two Tremblays
 *   distinguishable.
 * * **invited** — `fullName` is `''` until they set a password, so the address
 *   is the only thing that exists and it becomes the label.
 * * **neither** — returns `null` rather than a dash, so the caller decides what
 *   an unknown holder looks like instead of this inventing one.
 *
 * The name check is `trim()`, not truthiness: the wire sends an **empty
 * string** for an invited seat, and `seat.user &&` would have printed a blank
 * row — the same `—` bug one layer along, arriving through a shape change
 * rather than a missing field.
 */
export function seatHolder(seat) {
    const name = seat.user?.fullName.trim();
    if (name)
        return { name, email: seat.user.email };
    const address = seat.email ?? seat.user?.email;
    return address ? { name: address, email: null } : null;
}
//# sourceMappingURL=seats.js.map